import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import '../../css/HRCertificates.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown , faFileLines} from '@fortawesome/free-solid-svg-icons';

const loadingGif = process.env.REACT_APP_PUBLIC_URL + '/loading1.gif';

const HRCertificateForm = ({ language, data, isDisabled, onSendMessage }) => {
  const [certificateType, setCertificateType] = useState(data?.type);
  const [toAddress, setToAddress] = useState(data?.toAddress);
  const [numberOfCopies, setNumberOfCopies] = useState(data?.noOfCopies ? data.noOfCopies : 1);
  const { t } = useTranslation();
  const [certificateLanguage, setCertificateLanguage] = useState(data?.certificateLanguage?.toLowerCase() || language);
  const [isFormDisabled, setIsFormDisabled] = useState(isDisabled);
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [certificateTypes, setCertificateTypes] = useState([]);
  const [loadingCounter, setLoadingCounter] = useState(1);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [selectedCertificateText, setSelectedCertificateText] = useState('');
  const [userCanDownloadCertificate, setUserCanDownloadCertificate] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const decrementLoadingCounter = () => {
    setLoadingCounter(prevCount => {
        const newCount = prevCount - 1;
        if (newCount === 0) {
            setIsFirstLoading(false);  // Only set loading to false when all async operations are done
        }
        return newCount;
    });
  };

  useEffect(() => {
    setIsFormDisabled(isDisabled);
  }, [isDisabled]);

  useEffect(() => {
    const fetchCertificateTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/middleware/getRequestTypes?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          params: {
            assertion: localStorage.getItem('id_token'),
            sessionID: sessionStorage.getItem('sessionID'),
            language: localStorage.getItem('language'),
            frontendLog: JSON.stringify({
              sessionID: sessionStorage.getItem('sessionID'),
              EmployeeID: localStorage.getItem('EmployeeID'),
              channel: isMobile ? 'Mobile' : 'Portal',
              language: localStorage.getItem('language'),
            }),
          },
        });
        setUserCanDownloadCertificate(true);
        setErrorMessage("");
        setCertificateTypes(response.data); // Assuming response.data is an array of certificate types
      } catch (error) {
        setUserCanDownloadCertificate(false);
        if (error.response && error.response.data) {
          setErrorMessage(error.response.data.error || t('An error occurred, please try again.'));
        } else {
          setErrorMessage(t('An error occurred while fetching data.'));
        }
        console.error('Error fetching certificate types:', error);
      } finally {
        decrementLoadingCounter();  // Decrement the counter after this operation completes
      }
    };

    fetchCertificateTypes();
  }, []);

  useEffect(() => {
    if (!data?.toAddress) {
      if (certificateLanguage === 'ar') {
        setToAddress('إلى من يهمه الأمر');
      } else {
        setToAddress('To Whom it May Concern');
      }
    }
  }, [certificateLanguage, data?.toAddress]);

  useEffect(() => {
    if (certificateTypes.length > 0 && certificateType) {
      const selectedOption = certificateTypes.find(type => type.Formid === certificateType);
      setSelectedCertificateText(selectedOption?.Formtext || '');
    } else if (certificateTypes.length > 0 && !certificateType) {
      // Set default to the first certificate type if none is selected initially
      const defaultOption = certificateTypes[0];
      setCertificateType(defaultOption?.Formid);
      setSelectedCertificateText(defaultOption?.Formtext);
    }
  }, [certificateTypes, certificateType]);

  const validateForm = () => {
    if (!certificateType) {
      setValidationMessage(t('Certificate type is required.'));
      return false;
    }
    if (!toAddress) {
      setValidationMessage(t('To Address is required.'));
      return false;
    }
    if (!numberOfCopies || numberOfCopies < 1) {
      setValidationMessage(t('Number of copies must be at least 1.'));
      return false;
    }
    setValidationMessage(''); // Clear any previous messages if validation passes
    return true;
  };

  const handleDownload = () => {
    // Perform validation
    if (!validateForm()) {
      return; // Exit if validation fails
    }

    setIsFormDisabled(true);
    setIsLoading(true);

    const url = `${process.env.REACT_APP_BACKEND_URL}/middleware/downloadCertificate?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}`;

    axios
      .get(url, {
        params: {
          formid: certificateType,
          adrs: toAddress,
          langu: certificateLanguage,
          assertion: localStorage.getItem('id_token'),
          sessionID: sessionStorage.getItem('sessionID'),
          frontendLog: JSON.stringify({
            sessionID: sessionStorage.getItem('sessionID'),
            EmployeeID: localStorage.getItem('EmployeeID'),
            channel: isMobile ? 'Mobile' : 'Portal',
            language: localStorage.getItem('language'),
          }),
        },
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `${selectedCertificateText}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setIsLoading(false);
        onSendMessage({
          sender: 'bot',
          text: t('Certificate has been downloaded successfully. Do you need any assistance?'),
          clearHistory: true
        });
        setTimeout(() => {
          onSendMessage({
            sender: 'bot',
            isRating: true,
            requestType: "success",
            timestamp: new Date().getTime(),
          });
        }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));
        sessionStorage.setItem('clearHistory', true)
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Error downloading certificate:', error);
        onSendMessage({
            sender: 'bot',
            text: t('There was an error occurred, Please try again.'),
          });
      });
  };

  return (
    <div className={`certificate-form-container ${isFormDisabled ? 'form-disabled' : ''}`}>
      <div className="form-header">
        <h3 className="form-title">
          {/* <FontAwesomeIcon className='big-size' icon={faFileLines} /> */}
           {t('HR Certificates')}
        </h3>
      </div>
      {(isLoading || isFirstLoading) && (
        <div className="centered-v-h loading-overlay">
          <img className="img-bottom" src={loadingGif} alt="Loading..." width={70} height={70} />
          {isLoading && <span className="span-bottom">{t('Processing, please wait ...')}</span>}
        </div>
      )}
      {userCanDownloadCertificate ?
      <>
      <div className="form-group">
        <label htmlFor="formCertificateType">{t('Certificate Type')}</label>
        <div className='flex-row-cer'>

        <select
            id="formCertificateType"
            className="form-control dropdown-types"
            value={certificateType}
            onChange={(e) => {
              const selectedOption = certificateTypes.find(type => type.Formid === e.target.value);
              setCertificateType(selectedOption?.Formid);
              setSelectedCertificateText(selectedOption?.Formtext);
            }}
            disabled={isFormDisabled}
          >
            {certificateTypes.map((type) => (
              <option key={type.Formid} value={type.Formid}>{type.Formtext}</option>
            ))}
          </select>
        <div className="language-buttons">
          <button
            type="button"
            className={`btn ${certificateLanguage === 'ar' ? 'active' : ''}`}
            onClick={() => setCertificateLanguage('ar')}
            disabled={isFormDisabled}
          >
            {t('Arabic')}
          </button>
          <div className="divider-button"></div>
          <button
            type="button"
            className={`btn ${certificateLanguage === 'en' ? 'active' : ''}`}
            onClick={() => setCertificateLanguage('en')}
            disabled={isFormDisabled}
          >
            {t('English')}
          </button>
        </div>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="formToAddress">{t('To Address')}</label>
        <input
          type="text"
          id="formToAddress"
          className="form-control"
          value={toAddress}
          disabled={isFormDisabled}
          onChange={(e) => setToAddress(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="formNumberOfCopies">{t('Number of copies')}</label>
        <input
          type="number"
          id="formNumberOfCopies"
          className="form-control"
          value={numberOfCopies}
          min="1"
          onChange={(e) => setNumberOfCopies(e.target.value)}
          disabled={isFormDisabled}
        />
      </div>

      {validationMessage && <div className="red-span mb-3">{validationMessage}</div>}
    
      <div className='center-button'>
        <button
            type="button"
            disabled={isFormDisabled}
            className={`btn download-button ${language === 'ar' ? 'download-button-ar' : 'download-button-en'}`}
            onClick={handleDownload}
            >
            {/* <FontAwesomeIcon className="download-icon" icon={faFileArrowDown} /> */}
            {t('Print Electonic Certificate')}
        </button>
      </div>
      </>
      : 
      <div>
        {errorMessage}
      </div>
      }
    </div>
  );
};
export default HRCertificateForm;
